"use client";
import { Modal } from "@components/atoms/Modal";
import Button from "@components/atoms/Button";
import styles from "./IntentModal.module.scss";
import ColorButton from "../ColorSwitchButton";
import { useAppSelector, useAppDispatch } from "@lib/redux/hooks";
import { updateProfileAsync } from "@lib/redux/slices/profileSlice";
import { useState, useEffect, useRef } from "react";
import { PageType } from "@constants/pageTypes";
import { usePathname } from "next/navigation";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import {
  prepareLeadDataForCapture,
  submitPreparedLeadForCapture,
} from "@helpers/leadCapture";
import { getCarAvailability } from "@utils/carAvailability";
import { setLeadData } from "@utils/leadDataStorage";
import type { ProfileData } from "@lib/redux/slices/profileSlice";
import { getCustomerPhoneNumber } from "@utils/utils";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { inApp } from "@utils/platformDetection.client";

const DEFAULT_SELECTED_INTENT = "Still considering my options";

function truProfileIntentUrl(): string {
  const prefix = process.env.NEXT_PUBLIC_BASE_TERM
    ? `/${process.env.NEXT_PUBLIC_BASE_TERM}`
    : "";
  return `${typeof window !== "undefined" ? window.location.origin : ""}${prefix}/api/tru-profile/intent`;
}

async function postIntentTruProfile(
  intentValue: string,
  profile: ProfileData,
): Promise<void> {
  try {
    await fetch(truProfileIntentUrl(), {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        intent_buy_new_car: intentValue,
        first_name: (profile.name || "").trim(),
        primary_phone_number: profile.contact_no || profile.phone || "",
        personal_email: profile.email || "",
      }),
    });
  } catch {
    /* non-blocking */
  }
}

interface CarDetails {
  name: string;
  color: {
    name: string;
    hex: string[];
    type: string;
  };
}

interface IntentModalProps {
  isOpen: boolean;
  onClose: () => void;
  carDetails?: CarDetails;
  onIntentSelect?: (intent: string) => void;
  onIntentSelectionComplete?: () => void;
  onRequestCallback?: (intent: string) => void;
  onGetQuote?: (intent: string) => void;
  mmvDetails?: any;
  buyingIntent: any;
  cityName: string;
  highPriorityCapture?: boolean;
  variant?: "full" | "simple"; // New prop for variant
  pageType?: PageType | null;
  loginType?: string | null;
  disableClose?: boolean;
}

const IntentModal = ({
  isOpen,
  onClose,
  carDetails,
  onIntentSelect,
  onIntentSelectionComplete,
  onRequestCallback,
  onGetQuote,
  buyingIntent,
  cityName,
  mmvDetails,
  highPriorityCapture,
  variant = "full", // Default to full variant
  pageType,
  loginType,
  disableClose = false,
}: IntentModalProps) => {
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();
  const hasTrackedOpen = useRef(false);

  const isInApp = inApp();

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);
  // Access Redux state directly
  const userProfile = useAppSelector((state) => state.profile.data);
  const dispatch = useAppDispatch();

  const contactNo =
    userProfile?.contact_no || userProfile?.phone || getCustomerPhoneNumber();
  const buyingPeriod = userProfile?.buying_period || "";
  const hasValidBuyingPeriod =
    buyingPeriod.length > 0 &&
    buyingPeriod !== "Not Asked" &&
    buyingPeriod !== "None";

  useEffect(() => {
    if (!isOpen) {
      hasTrackedOpen.current = false;
      return;
    }

    if (hasTrackedOpen.current) {
      return;
    }

    hasTrackedOpen.current = true;
    void trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
      pageType: pageType || "Intent",
      additionalProps: {
        view_type: "view_popup",
        view_name: "new_car_intent",
        modal_name: "IntentModal",
        modal_variant: variant,
        city_name: cityName,
        vehicle_make: mmvDetails?.make || "",
        vehicle_model: mmvDetails?.model || "",
        vehicle_color: mmvDetails?.color || "",
        vehicle_variant: mmvDetails?.variant || "",
        customer_name: userProfile?.name || "",
        email: userProfile?.email || "",
        phone: contactNo || "",
        login_type: loginType || sessionStorage.getItem("loginType") || "",
      },
    });
  }, [isOpen]);

  const { buying_intent: intentData } = buyingIntent || {};
  let intentQuestion = "";
  let intentOptions = [];

  if (hasValidBuyingPeriod) {
    intentOptions = intentData?.secondary_popup?.option1 || [];
    intentQuestion = intentData?.secondary_popup?.question;
  } else {
    intentOptions = intentData?.primary_popup?.option1 || [];
    intentQuestion = intentData?.primary_popup?.question;
  }

  const defaultSelectedIntent =
    intentOptions?.[2]?.value || DEFAULT_SELECTED_INTENT;

  const [selectedIntent, setSelectedIntent] = useState<string>(
    DEFAULT_SELECTED_INTENT,
  );

  useEffect(() => {
    setSelectedIntent(defaultSelectedIntent);
  }, [defaultSelectedIntent]);

  const handleIntentSelection = async (
    selectedValue: string,
  ): Promise<boolean> => {
    const resolvedLoginType =
      loginType || sessionStorage.getItem("loginType") || "Unknown";
    const leadTrackingProps = {
      action_type: "lead_capture",
      modal_name: "IntentModal",
      modal_variant: variant,
      city_name: cityName,
      vehicle_make: mmvDetails?.make || "",
      vehicle_model: mmvDetails?.model || "",
      vehicle_color: mmvDetails?.color || "",
      vehicle_variant: mmvDetails?.variant || "",
      customer_name: userProfile?.name || "",
      email: userProfile?.email || "",
      phone: contactNo || "",
      login_type: resolvedLoginType,
      intent: selectedValue,
    };

    try {
      const leadData = await prepareLeadDataForCapture({
        pageType: pageType || "",
        brandName: mmvDetails?.make,
        modelName: mmvDetails?.model,
        variantName: mmvDetails?.variant,
        cityName,
        vehicleColor: mmvDetails?.color || "",
        loginTypeLabel: resolvedLoginType,
        componentName: "IntentModal",
        baseLeadData: {
          phone: contactNo,
        },
        buyingPeriod: selectedValue,
        onLeadDataMerged: setLeadData,
      });

      if (!leadData) {
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType || "Intent",
          additionalProps: {
            exception_type: "api_error",
            exception_name: "field callback request",
            ...leadTrackingProps,
          },
        });
        return false;
      }

      const fetchData = {
        qualification_update_data: {
          buying_period: selectedValue,
        },
      };
      const buyingPeriod = selectedValue || "";

      const [leadResponse, profileUpdateResult] = await Promise.all([
        submitPreparedLeadForCapture({
          pageType: pageType || "",
          brandName: mmvDetails?.make,
          modelName: mmvDetails?.model,
          variantName: mmvDetails?.variant,
          cityName,
          vehicleColor: mmvDetails?.color || "",
          leadData,
          buyingPeriod,
          loginTypeLabel: resolvedLoginType,
          componentName: "IntentModal",
        }),
        dispatch(
          updateProfileAsync({
            profileUpdates: { buying_period: selectedValue },
            apiPayload: fetchData,
          }),
        ),
      ]);

      if (!leadResponse?.success) {
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType || "Intent",
          additionalProps: {
            exception_type: "api_error",
            exception_name: "field callback request",
            ...leadTrackingProps,
          },
        });
        return false;
      }

      if (!updateProfileAsync.fulfilled.match(profileUpdateResult)) {
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType || "Intent",
          additionalProps: {
            exception_type: "api_error",
            exception_name: "field callback request",
            ...leadTrackingProps,
          },
        });
      }

      sessionStorage.setItem("buying_intent", "false");
      return true;
    } catch (error) {
      await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
        pageType: pageType || "Intent",
        additionalProps: {
          exception_type: "api_error",
          exception_name: "field callback request",
          error: error instanceof Error ? error.message : "Unknown error",
          ...leadTrackingProps,
        },
      });
      return false;
    }
  };

  const handleChipSelect = async (intent: string) => {
    setSelectedIntent(intent);
    onIntentSelect?.(intent);
    if (variant === "simple" && userProfile) {
      void postIntentTruProfile(intent, userProfile);
    }

    // For simple variant, auto-execute on selection and close
    if (variant === "simple") {
      try {
        const canContinue = await handleIntentSelection(intent);
        if (!canContinue) {
          return;
        }
        onGetQuote?.(intent);
        onClose();
      } finally {
        onIntentSelectionComplete?.();
      }
    }
  };

  const handleRequestCallback = async () => {
    const canContinue = await handleIntentSelection(selectedIntent);
    if (!canContinue) {
      return;
    }
    if (userProfile) {
      void postIntentTruProfile(selectedIntent, userProfile);
    }
    onRequestCallback?.(selectedIntent);
    onClose();
  };

  const handleGetQuote = async () => {
    showLoader("Loading Quote...");
    const canContinue = await handleIntentSelection(selectedIntent);
    if (!canContinue) {
      hideLoader();
      return;
    }
    if (userProfile) {
      void postIntentTruProfile(selectedIntent, userProfile);
    }
    onGetQuote?.(selectedIntent);
    onClose();
  };

  // Custom close handler that only allows closing if a selection was made
  const handleModalClose = () => {
    if (disableClose) {
      return;
    }

    // For simple variant, don't allow manual close - only through selection
    if (variant === "simple") {
      return;
    }

    // For full variant, only allow close if an intent is selected
    if (selectedIntent) {
      void trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType || "Intent",
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_close_intent",
          modal_name: "IntentModal",
          modal_variant: variant,
          city_name: cityName,
          vehicle_make: mmvDetails?.make || "",
          vehicle_model: mmvDetails?.model || "",
          vehicle_color: mmvDetails?.color || "",
          vehicle_variant: mmvDetails?.variant || "",
          customer_name: userProfile?.name || "",
          email: userProfile?.email || "",
          phone: contactNo || "",
          login_type: loginType || sessionStorage.getItem("loginType") || "",
          intent: selectedIntent,
        },
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={isInApp ? "When are you planning to buy this car?" : intentQuestion}
      className={styles.intentModal}
      showCloseButton={variant === "full" && !!selectedIntent && !disableClose}
      overlayClassName={styles.intentModalOverlay}
    >
      <div className={styles.modalContent}>

        {/* Intent Selection Chips */}
        <div className={styles.intentChips}>
          {intentOptions?.map(
            (
              option: {
                value: string;
                createLead: string;
              },
              idx: number,
            ) => (
              <button
                key={`${option}-${idx}`}
                className={`${styles.chip} ${
                  selectedIntent?.replace(/\s/g, "") ===
                  option?.value?.replace(/\s/g, "")
                    ? styles.selected
                    : ""
                }`}
                onClick={() => handleChipSelect(option?.value)}
              >
                {option?.value}
              </button>
            ),
          )}
        </div>

        {/* Action Buttons - Only show in full variant */}
        {variant === "full" && (
          <div className={styles.actionButtons}>
            <Button
              className={`${styles.callbackButton} ${
                !selectedIntent ? styles.disabled : ""
              }`}
              onClick={handleRequestCallback}
              disabled={!selectedIntent}
            >
              Request&nbsp;callback
            </Button>
            <Button
              className={`${styles.quoteButton} ${
                !selectedIntent ? styles.disabled : ""
              }`}
              onClick={handleGetQuote}
              disabled={!selectedIntent}
            >
              Get quote
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default IntentModal;
